const mobileRe = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i

// Optional chaining only guards null/undefined — it does NOT protect against a
// *throwing* property getter. Injected wallets (especially when several fight
// over window.ethereum) can define `ethereum`/`request` as a getter that throws
// on access; reading it during render then white-screens the whole page. Wrap
// every provider probe in try/catch so a hostile provider degrades to
// "no provider" instead of crashing mount. (Fixes wallet-in-app-browser white screen.)
const hasEvmProvider = () => {
  try {
    return Boolean(globalThis.window?.ethereum?.request)
  } catch (_) {
    return false
  }
}

const hasSolanaProvider = () => {
  try {
    return Boolean(
      globalThis.window?.solana ||
        globalThis.window?.phantom?.solana ||
        globalThis.window?.solflare?.solana,
    )
  } catch (_) {
    return false
  }
}

const isCrossOriginIframe = () => {
  try {
    if (globalThis.window.self === globalThis.window.top) return false
    void globalThis.window.top.location.href
    return false
  } catch (_) {
    return true
  }
}

// detect() runs synchronously inside a React useState initializer (render phase),
// so it must NEVER throw — any exception here white-screens the page via the
// ErrorBoundary. Each sub-probe is independently guarded; the whole body is also
// wrapped so a fault degrades to a safe "no wallet" environment.
const detect = () => {
  try {
    const isMobile = mobileRe.test(globalThis.navigator?.userAgent || "")
    const crossOriginIframe = isCrossOriginIframe()
    const ethereumProvider = hasEvmProvider()
    const solanaProvider = hasSolanaProvider()

    let isIframe = false
    try {
      isIframe = globalThis.window?.self !== globalThis.window?.top
    } catch (_) {
      // Cross-origin top access can throw — that itself means we're in an iframe.
      isIframe = true
    }

    return {
      isMobile,
      isIframe,
      isCrossOriginIframe: crossOriginIframe,
      hasEthereumProvider: ethereumProvider,
      hasSolanaProvider: solanaProvider,
      hasProvider: ethereumProvider || solanaProvider,
      canConnectWallet: !crossOriginIframe && (ethereumProvider || solanaProvider),
    }
  } catch (_) {
    return {
      isMobile: false,
      isIframe: false,
      isCrossOriginIframe: false,
      hasEthereumProvider: false,
      hasSolanaProvider: false,
      hasProvider: false,
      canConnectWallet: false,
    }
  }
}

const addEip6963Listener = callback => {
  const handler = event => {
    if (event?.detail?.provider) callback(detect())
  }
  globalThis.window?.addEventListener?.("eip6963:announceProvider", handler)
  try {
    globalThis.window?.dispatchEvent?.(new Event("eip6963:requestProvider"))
  } catch (_) {}
  return () => globalThis.window?.removeEventListener?.("eip6963:announceProvider", handler)
}

const currentHref = () => globalThis.window?.location?.href || ""

const queryParam = key => {
  try {
    return new URLSearchParams(globalThis.window?.location?.search || "").get(key) || ""
  } catch (_) {
    return ""
  }
}

const routePaymentId = () => {
  const match = (globalThis.window?.location?.pathname || "").match(/^\/q\/([^/?#]+)/)
  return match ? decodeURIComponent(match[1]) : ""
}

const continueOnTop = url => {
  if (!url) return
  try {
    globalThis.window.top.location.href = url
  } catch (_) {
    globalThis.window.location.href = url
  }
}

const handoffUrl = (paymentId, token) => {
  const origin = globalThis.window?.location?.origin || ""
  const id = encodeURIComponent(paymentId || "")
  const t = encodeURIComponent(token || "")
  const m = encodeURIComponent(queryParam("m") || "")
  return `${origin}/q/${id}?continue=${id}${t ? `&t=${t}` : ""}${m ? `&m=${m}` : ""}`
}

const uniqueActionId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

const stripSchemeForMetamask = url => url.replace(/^https?:\/\//i, "")

const walletLinks = dappUrl => {
  const encoded = encodeURIComponent(dappUrl)
  const encodedNoScheme = encodeURIComponent(stripSchemeForMetamask(dappUrl))

  return [
    {
      id: "metamask",
      label: "MetaMask",
      href: `https://link.metamask.io/dapp/${encodedNoScheme}`,
      source:
        "https://docs.metamask.io/metamask-connect/supported-platforms/ (MetaMask in-app browser: https://link.metamask.io/dapp/{url})",
    },
    {
      id: "okx",
      label: "OKX Wallet",
      href: `okx://wallet/dapp/url?dappUrl=${encoded}`,
      source:
        "https://web3.okx.com/onchainos/docs/waas/app-universal-link (OKX DApp deeplink: okx://wallet/dapp/url?dappUrl=...)",
    },
    {
      id: "bitget",
      label: "Bitget Wallet",
      href: `https://bkcode.vip?version=2.0&action=DApp&actionID=${encodeURIComponent(
        uniqueActionId(),
      )}&url=${encoded}`,
      source:
        "https://web3.bitget.com/en/docs/configuration/deeplink (Open DApp: action=DApp, url required; Android supports https://bkcode.vip?{params})",
    },
    {
      id: "imtoken",
      label: "imToken",
      href: `imtokenv2://navigate/DappView?url=${encoded}`,
      source:
        "https://imtoken.gitbook.io/developers/products/deep-linking (Reference Routes: DappView?url=<URL>)",
    },
    {
      id: "trust",
      label: "Trust Wallet",
      href: `https://link.trustwallet.com/open_url?coin_id=60&url=${encoded}`,
      source:
        "https://developer.trustwallet.com/developer/develop-for-trust/deeplinking (DApp Browser: /open_url?coin_id=60&url=...)",
    },
  ]
}

export {
  addEip6963Listener,
  continueOnTop,
  currentHref,
  detect,
  handoffUrl,
  queryParam,
  routePaymentId,
  walletLinks,
}
